import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  crmApi,
  extractCrmError,
  userHasCrmAccess,
  CLIENT_STATUSES,
} from "@/lib/crmApi";
import { formatCrmDateTime } from "@/utils/crmDateTime";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Filter,
  MessageSquare,
  Phone,
  SlidersHorizontal,
  Users,
  Wrench,
  BellRing,
} from "lucide-react";

const SORTS = [
  { value: "last_message_desc", label: "По последнему сообщению" },
  { value: "created_desc", label: "По дате создания" },
  { value: "updated_desc", label: "По дате обновления" },
  { value: "name_asc", label: "По имени" },
];

function StatusBadge({ status }) {
  const map = {
    new: "border-blue-200 bg-blue-50 text-blue-700",
    reviewed: "border-slate-200 bg-slate-50 text-slate-700",
    in_progress: "border-amber-200 bg-amber-50 text-amber-700",
    exported: "border-emerald-200 bg-emerald-50 text-emerald-700",
    deal: "border-green-200 bg-green-50 text-green-700",
    ignored: "border-gray-200 bg-gray-50 text-gray-500",
    blocked: "border-red-200 bg-red-50 text-red-700",
  };
  const label = CLIENT_STATUSES.find((s) => s.value === status)?.label || status;
  return (
    <Badge
      variant="outline"
      className={`rounded-full px-2.5 py-1 font-medium ${map[status] || "border-gray-200 bg-gray-50 text-gray-700"}`}
    >
      {label}
    </Badge>
  );
}

function formatRel(dt) {
  return formatCrmDateTime(dt);
}

function getClientName(client) {
  return client.manual_name || client.customer_name || client.display_name || "Без имени";
}

function getVehicleLabel(client) {
  const vehicle = client.primary_vehicle;
  if (vehicle) {
    const model = `${vehicle.brand || ""} ${vehicle.model || ""}`.trim();
    return [model, vehicle.year].filter(Boolean).join(", ") || "Автомобиль не указан";
  }
  return client.detected_car_model || "Автомобиль не указан";
}

function getInitials(name) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function StatCard({ label, value, hint, icon: Icon }) {
  return (
    <Card className="border-slate-200 bg-white shadow-sm">
      <CardContent className="flex items-center gap-3 p-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-slate-700">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
          <p className="text-2xl font-semibold text-slate-950">{value}</p>
          {hint && <p className="text-xs text-slate-500">{hint}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function CrmClientsPage() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [healthState, setHealthState] = useState(null); // {enabled, ok, error}
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const q = searchParams.get("q") || "";
  const status = searchParams.get("status") || "";
  const stockWait = searchParams.get("stock_wait") === "1";
  const sort = searchParams.get("sort") || "last_message_desc";
  const page = parseInt(searchParams.get("page") || "1", 10);
  const pageSize = 20;

  const [searchInput, setSearchInput] = useState(q);

  useEffect(() => {
    crmApi
      .health()
      .then(setHealthState)
      .catch((e) =>
        setHealthState({ enabled: false, ok: false, error: extractCrmError(e) })
      );
  }, []);

  const fetchClients = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const params = { page, page_size: pageSize, sort };
      if (q) params.q = q;
      if (status) params.status = status;
      if (stockWait) params.has_active_stock_wait = true;
      const body = await crmApi.listClients(token, params);
      setData(body);
    } catch (e) {
      setError(extractCrmError(e, "Не удалось загрузить клиентов"));
    } finally {
      setLoading(false);
    }
  }, [token, page, pageSize, sort, q, status, stockWait]);

  useEffect(() => {
    if (!token) return;
    fetchClients();
  }, [token, fetchClients]);

  useEffect(() => {
    if (!authLoading && !token) {
      setLoading(false);
    }
  }, [authLoading, token]);

  useEffect(() => {
    if (!authLoading && !userHasCrmAccess(user)) {
      navigate("/");
    }
  }, [authLoading, user, navigate]);

  const clientItems = data?.items ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;
  const hasActiveFilters = Boolean(q || status || stockWait || sort !== "last_message_desc");
  const pageStats = useMemo(() => {
    return {
      shown: clientItems.length,
      inWork: clientItems.filter((item) => item.status === "in_progress").length,
      withoutPhone: clientItems.filter((item) => !item.phone).length,
      messages: clientItems.reduce((sum, item) => sum + (item.messages_count || 0), 0),
    };
  }, [clientItems]);

  const onSubmitSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (searchInput) next.set("q", searchInput);
    else next.delete("q");
    next.set("page", "1");
    setSearchParams(next);
  };
  const onChangeFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    next.set("page", "1");
    setSearchParams(next);
  };
  const gotoPage = (p) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(p));
    setSearchParams(next);
  };
  const resetFilters = () => {
    setSearchInput("");
    setSearchParams(new URLSearchParams());
  };

  // CRM disabled / postgres down — отдельные состояния.
  if (healthState && !healthState.enabled) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="text-amber-500" /> CRM-модуль отключён
            </CardTitle>
            <CardDescription>
              Установите <code>CRM_MODULE_ENABLED=true</code> и перезапустите сервер.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-500">
              Подробности: <code>{healthState.error || "смотрите журналы сервера"}</code>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (healthState && healthState.enabled && !healthState.ok) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="text-red-500" /> PostgreSQL недоступна
            </CardTitle>
            <CardDescription>{healthState.error || "Ошибка подключения"}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (!authLoading && !userHasCrmAccess(user)) {
    return null;
  }

  return (
    <div className="mx-auto max-w-7xl p-4 sm:p-6">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <Badge variant="outline" className="mb-3 border-slate-200 bg-white text-slate-600">
            Рабочее место менеджера
          </Badge>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-950">Клиенты CRM</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-500">
            Единый реестр клиентов из WhatsApp: статусы, автомобили, сообщения и быстрый переход в карточку.
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm">
          {data ? `Всего клиентов: ${data.total}` : "Загружаем данные..."}
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="В таблице" value={pageStats.shown} hint="на текущей странице" icon={Users} />
        <StatCard label="В работе" value={pageStats.inWork} hint="требуют внимания" icon={SlidersHorizontal} />
        <StatCard label="Сообщения" value={pageStats.messages} hint="в текущей выборке" icon={MessageSquare} />
        <StatCard label="Без телефона" value={pageStats.withoutPhone} hint="нужно уточнить" icon={Phone} />
      </div>

      <Card className="mb-4 border-slate-200 bg-white shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base text-slate-900">
            <Filter className="h-4 w-4 text-slate-500" />
            Фильтры и поиск
          </CardTitle>
          <CardDescription>Ищите по имени, телефону, автомобилю или идентификатору чата.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmitSearch} className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_180px_230px_auto_auto_auto]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                className="pl-9"
                placeholder="Имя, телефон, авто или ID чата"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <Select value={status || "_all"} onValueChange={(v) => onChangeFilter("status", v === "_all" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="Статус" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Все статусы</SelectItem>
                {CLIENT_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={(v) => onChangeFilter("sort", v)}>
              <SelectTrigger><SelectValue placeholder="Сортировка" /></SelectTrigger>
              <SelectContent>
                {SORTS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant={stockWait ? "default" : "outline"}
              className={stockWait ? "bg-amber-600 hover:bg-amber-700" : ""}
              onClick={() => onChangeFilter("stock_wait", stockWait ? "" : "1")}
              title="Клиенты с активным ожиданием поступления товара"
            >
              <BellRing className="h-4 w-4" />
              Ждут наличия
            </Button>
            <Button type="submit">Найти</Button>
            {hasActiveFilters && (
              <Button type="button" variant="outline" onClick={resetFilters}>
                Сбросить
              </Button>
            )}
          </form>
          <p className="mt-3 text-xs text-slate-500">
            Полный список «кто что ждёт» — в меню{" "}
            <Link to="/crm/stock-watches" className="font-medium text-amber-700 hover:underline">
              Ожидание наличия
            </Link>
            .
          </p>
        </CardContent>
      </Card>

      {error && (
        <Card className="mb-4 border-red-200 bg-red-50">
          <CardContent className="text-red-700 text-sm py-3">{error}</CardContent>
        </Card>
      )}

      <Card className="overflow-hidden border-slate-200 bg-white shadow-sm">
        <CardHeader className="border-b border-slate-100 pb-4">
          <CardTitle className="text-base text-slate-900">Список клиентов</CardTitle>
          <CardDescription>
            Откройте строку, чтобы увидеть переписку, запросы, сделки и автомобили клиента.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="h-14 w-full rounded-lg" />
              ))}
            </div>
          ) : error && !data ? (
            <div className="p-10 text-center text-red-700">
              <p className="font-medium">Не удалось загрузить клиентов</p>
              <p className="mt-2 text-sm">{error}</p>
              <Button type="button" variant="outline" className="mt-4" onClick={fetchClients}>
                Повторить
              </Button>
            </div>
          ) : !data ? (
            <div className="p-10 text-center text-slate-500 text-sm">Загружаем данные...</div>
          ) : clientItems.length === 0 ? (
            <div className="p-10 text-center text-slate-500">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                <Search className="h-5 w-5 text-slate-400" />
              </div>
              <p className="font-medium text-slate-900">Клиенты не найдены</p>
              <p className="mt-1 text-sm">Измените фильтры или поисковый запрос.</p>
            </div>
          ) : (
            <Table className="min-w-[780px]">
              <TableHeader className="bg-slate-50/80">
                <TableRow className="hover:bg-transparent">
                  <TableHead className="px-4">Клиент</TableHead>
                  <TableHead>Контакты</TableHead>
                  <TableHead>Автомобиль</TableHead>
                  <TableHead>Статус</TableHead>
                  <TableHead>Последняя активность</TableHead>
                  <TableHead className="text-right pr-4">Сообщения</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clientItems.map((c) => {
                  const name = getClientName(c);
                  const vehicle = getVehicleLabel(c);
                  const initials = getInitials(name) || "CRM";
                  return (
                    <TableRow
                      key={c.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(`/crm/clients/${c.id}`)}
                    >
                      <TableCell className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">
                            {initials}
                          </div>
                          <div className="min-w-0">
                            <Link
                              to={`/crm/clients/${c.id}`}
                              className="font-medium text-slate-950 hover:text-blue-700 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {name}
                            </Link>
                            <p className="truncate text-xs text-slate-500">{c.whatsapp_chat_id || "Чат не указан"}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-700">
                        {c.phone || <span className="text-slate-400">Телефон не указан</span>}
                      </TableCell>
                      <TableCell className="max-w-[240px]">
                        <div className="flex items-center gap-2 text-sm text-slate-700">
                          <Wrench className="h-4 w-4 shrink-0 text-slate-400" />
                          <span className="truncate">{vehicle}</span>
                        </div>
                      </TableCell>
                      <TableCell><StatusBadge status={c.status} /></TableCell>
                      <TableCell className="text-sm text-slate-600">{formatRel(c.last_message_at)}</TableCell>
                      <TableCell className="pr-4 text-right">
                        <span className="inline-flex min-w-8 justify-center rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">
                          {c.messages_count || 0}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Пагинация */}
      {data && totalPages > 1 && (
        <div className="mt-4 flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-slate-500">
            Страница {data.page} из {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => gotoPage(page - 1)}
            >
              <ChevronLeft className="h-4 w-4" /> Назад
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => gotoPage(page + 1)}
            >
              Вперёд <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
